import type { Editor } from '@tiptap/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import React from 'react';
import { useTeaContext } from '../contexts/tea-context';
import type { DocumentData } from '../data/.types/tea';
import { TeaStorageClient } from '../data/document/api/TeaStorageClient';
import type { MetaProperty } from '../data/document/api/createDocument';
import { prepareHtmlWithAnchoredHeadings } from '../helpers/prepareHtmlWithAnchoredHeadings';
import { TeaBroadcaster } from '../tea-broadcaster';
import { teaErrorNotifier } from './tea-error-notifier';

type TeaNetworkContextType = {
  teaStorage: TeaStorageClient;
  onError: (error: Error) => void;
  documentUuid: TeaNetworkProps['documentUuid'];
  meta: TeaNetworkProps['meta'];
  documentGroupId: TeaNetworkProps['documentGroupId'];
};

export const TeaNetworkContext = createContext<TeaNetworkContextType>(
  {} as TeaNetworkContextType,
);

type TeaNetworkProps = {
  accessToken: string;
  baseUrl: string;
  documentGroupId?: string;
  documentUuid: string;
  meta?: MetaProperty;
  onError?: (error: Error) => void;
};

export const TeaNetwork = ({
  accessToken,
  baseUrl,
  documentGroupId,
  documentUuid,
  meta = {},
  onError = teaErrorNotifier,
  children,
}: TeaNetworkProps & { children: React.ReactNode | React.ReactNode[] }) => {
  validateTeaNetworkProps({ baseUrl, accessToken, documentUuid });
  validateTeaNetworkChildren(children);

  const teaStorage = useMemo(
    () =>
      new TeaStorageClient(
        accessToken,
        baseUrl,
        documentGroupId,
        documentUuid,
        window.location.toString(),
        meta,
      ),
    [accessToken, baseUrl, documentGroupId, documentUuid, meta],
  );

  return (
    <TeaNetworkContext.Provider
      value={{
        documentUuid,
        onError,
        teaStorage,
        meta,
        documentGroupId,
      }}
    >
      {children}
    </TeaNetworkContext.Provider>
  );
};

TeaNetwork.LoadDocument = () => {
  const { teaStorage } = useContext(TeaNetworkContext);
  const { isLoading } = useTeaStorageOperationWithLoading(() =>
    teaStorage.loadDocument(),
  );
  return <>{isLoading ? <div data-testid="loading">Loading...</div> : null}</>;
};

TeaNetwork.CreateDocument = () => {
  const { teaStorage } = useContext(TeaNetworkContext);
  const { isLoading } = useTeaStorageOperationWithLoading(() =>
    teaStorage.createDocument(),
  );
  return <>{isLoading ? <div data-testid="loading">Loading...</div> : null}</>;
};

TeaNetwork.SaveDocument = () => {
  const { editor, editorFormats } = useTeaContext();
  const { teaStorage, documentUuid, documentGroupId, meta } =
    useContext(TeaNetworkContext);

  const onSaveHandler = useCallback(
    async ({ editor }: { editor: Editor }) => {
      const broadcaster = new TeaBroadcaster(documentUuid, editor);

      const html = editorFormats?.heading?.allowAnchoredHeadings
        ? prepareHtmlWithAnchoredHeadings(editor.getHTML())
        : editor.getHTML();

      broadcaster?.publishSaving({
        html,
        document: editor.getJSON(),
      });

      await teaStorage.saveDocument({
        data: {
          id: documentUuid,
          type: 'document',
          attributes: {
            document: JSON.stringify(editor.getJSON()),
            html,
            text: editor.getText(),
            meta: meta ?? {},
            documentGroupId,
          },
        },
      });

      broadcaster?.publishSaved({
        html,
        document: editor.getJSON(),
      });
    },
    [
      meta,
      documentUuid,
      teaStorage.saveDocument,
      editorFormats,
      documentGroupId,
    ],
  );

  useEffect(() => {
    if (!editor) return;
    editor.on('update', onSaveHandler);
    return () => {
      editor.off('update', onSaveHandler);
    };
  }, [editor, onSaveHandler]);

  // There is currently no UI for this component to render - in the future we
  // may choose to implement some loading UI like we do for loading and creating
  return null;
};

/**
 * Hook to handle common Tea storage operations with loading state management
 * @param operation - The storage operation function to execute (e.g., teaStorage.loadDocument)
 * @returns Loading state and operation result
 */
export function useTeaStorageOperationWithLoading<T extends DocumentData>(
  operation: () => Promise<T | null>,
) {
  const { editor } = useTeaContext();
  const { onError } = useContext(TeaNetworkContext);

  const [isLoading, setIsLoading] = useState(false);

  const requestRef = useRef<Promise<T | null> | null>(null);

  useEffect(() => {
    if (requestRef.current !== null) return;

    const executeOperation = async () => {
      if (!editor) return;

      try {
        requestRef.current = operation();
        setIsLoading(true);
        const data = await requestRef.current;

        if (data === null) {
          throw new Error('TeaStorage operation returned a `null` response');
        }

        editor.commands.setContent(
          data.document ?? data.html ?? data.text ?? '',
        );
      } catch (error) {
        if (error instanceof Error) {
          onError(error);
        }
        onError(new Error(JSON.stringify(error)));
      } finally {
        requestRef.current = null;
        setIsLoading(false);
      }
    };

    executeOperation();
  }, [operation, editor, onError]);

  return { isLoading };
}

const validateTeaNetworkProps = ({
  baseUrl,
  accessToken,
  documentUuid,
}: TeaNetworkProps) => {
  if (typeof baseUrl !== 'string') {
    throw new Error('TeaNetwork: baseUrl is required');
  }
  if (!accessToken) throw new Error('TeaNetwork: accessToken is required');
  if (!documentUuid) throw new Error('TeaNetwork: documentUuid is required');
};

const validateTeaNetworkChildren = (
  children: React.ReactNode | React.ReactNode[],
) => {
  // Check if children exist and if they include TeaNetwork.CreateDocument
  const childrenArray = React.Children.toArray(children);
  const hasValidTeaNetworkChild = childrenArray.some(
    (child) =>
      React.isValidElement(child) &&
      (child.type === TeaNetwork.CreateDocument ||
        child.type === TeaNetwork.LoadDocument),
  );

  if (hasValidTeaNetworkChild === false) {
    throw new Error(
      'TeaNetwork must render at least one valid TeaNetwork child',
    );
  }
};
